import React from 'react';

import { ExternalLink } from 'lucide-react';

import { Badge } from '@/components/shadcn/ui/badge';
import { Checkbox } from '@/components/shadcn/ui/checkbox';
import { Currency } from '@/constants';
import { useTokenUsdPrices } from '@/hooks/useTokenUsdPrices';
import { ResultUtxo } from '@/networks/btc-like/common/transactions/api';
import { useScanUrlByCurrency } from '@/pages/History/SwapTable/Trackers/hooks';
import { cn, truncateMidAddressInputValue } from '@/utils';

import { UtxoStatus } from './UtxoStatus';
import { formatAmountToFiat, formatDecimalsAmount } from './utils';

interface UtxoSelectCardProps {
  isSelected: boolean;
  className?: string;
  utxo: ResultUtxo;
  onClick?: () => void;
  isShowFiatValue?: boolean;
  currency: Currency;
}

export const UtxoSelectCard: React.FC<UtxoSelectCardProps> = ({
  isSelected,
  className,
  utxo,
  onClick,
  isShowFiatValue = false,
  currency,
}) => {
  const { getTokenUsdPrice } = useTokenUsdPrices();
  const scanUrl = useScanUrlByCurrency(currency);

  const utxoValue = isShowFiatValue
    ? formatAmountToFiat(currency, utxo.value, getTokenUsdPrice(currency))
    : formatDecimalsAmount(currency, utxo.value);

  return (
    <div
      className={cn(
        'bg-foreground hover:bg-foreground/80 cursor-pointer rounded-lg border px-4 py-2 transition-all duration-100',
        isSelected && 'border-primary-purple bg-primary-purple/20 hover:bg-primary-purple/15',
        className
      )}
      onClick={onClick}
    >
      <div className="grid grid-cols-[auto_1fr] gap-4">
        <div className="flex items-center">
          <Checkbox checked={isSelected} />
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground align-middle text-xs font-medium">Utxo</span>
            <a
              onClick={(e) => e.stopPropagation()}
              href={scanUrl!.concat(utxo.txid).concat(`:${utxo.vout}`)}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Badge variant="default" className="flex items-center gap-0.5">
                <ExternalLink className="size-2.5" />
                {truncateMidAddressInputValue(utxo.txid)} <span className="text-muted-foreground">:{utxo.vout}</span>
              </Badge>
            </a>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-xs font-medium">Value</span>
            <span className="text-sm">{utxoValue}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground align-middle text-xs font-medium">Status</span>
            <UtxoStatus isConfirmed={utxo.status.confirmed} />
          </div>
        </div>
      </div>
    </div>
  );
};
